// Writing, editing and importing a recipe (redesign slice 7b; wireframes 18
// to 22; docs/2026-10-04-recipes-design.md, sections 2 to 4).
//
//   /recipes/new        write one: Basics, Ingredients, Steps, Details, then
//                       the review screen
//   /recipes/:id/edit   the same steps for a saved recipe; nothing changes
//                       until Save, so a half-done edit never reaches the
//                       plan or the grocery list
//   /recipes/import     what the Add sheet found at a link or in a file: one
//                       recipe opens on the review screen, several show as
//                       a list with "Add N recipes"
//
// What's typed is kept as a draft in the database (recipe_drafts, 0056),
// per person, about a second after typing stops: "new", the recipe's id, or
// "import". Coming back offers "Continue your draft" or "Start over".
// Saving deletes the draft.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { deleteRecipeDraft, loadRecipeDraft, saveRecipeDraft } from "../lib/db";
import { emptyRecipe, normalizeImportedRecipe } from "../lib/recipeUtils";
import { importGaps, readyToSave, reviewFlags, splitSourceLink, type EntryIngredient, type EntryStep } from "../lib/recipeEntry";
import { cuisinesIn } from "../lib/recipeLibrary";
import { COLORS, FONT_SIZE, SPACE } from "../lib/constants";
import type { Recipe } from "../lib/types";
import { BasicsStep, DetailsStep } from "../components/recipes/WriteParts";
import { primaryButton, secondaryButton, stepHeading } from "../components/recipes/writeStyles";
import { IngredientsStep } from "../components/recipes/IngredientsStep";
import { StepsStep } from "../components/recipes/StepsStep";
import { RecipeReview } from "../components/recipes/RecipeReview";
import { supporting } from "../components/recipeStyles";
import { textButton } from "../components/plan/planStyles";

type Mode = "new" | "edit" | "import";
type Step = 1 | 2 | 3 | 4 | "review";
type Gaps = { servings: boolean; times: boolean };
type Origin = { dedupRecipeId: string | null; normalizedUrl: string } | null;

interface Draft {
  recipe: Recipe;
  step: Step;
  unclear: string[]; // ingredient ids whose amount couldn't be read
  gaps: Gaps; // what an imported page left out
  origin: Origin; // set for a recipe imported from a link
}

// What the Add sheet hands over (location state).
export interface ImportHandoff {
  recipes: Record<string, unknown>[];
  urlMeta?: { dedupRecipeId: string | null; normalizedUrl: string };
}

const STEP_NAMES = { 1: "Basics", 2: "Ingredients", 3: "Steps", 4: "Details" } as const;
const NO_GAPS: Gaps = { servings: false, times: false };
const DRAFT_DELAY_MS = 900;

// A new recipe starts with no times, so the review screen can say they are
// missing rather than showing made-up ones.
const blankRecipe = () => ({ ...(emptyRecipe() as unknown as Recipe), activeTime: 0, cookTime: 0, ingredients: [], instructions: [] });

// An imported recipe in the app's shape, remembering what the page left out
// (the defaults filled in for a missing time would otherwise look real).
function fromImport(raw: Record<string, unknown>): Draft {
  const gaps = importGaps(raw);
  let recipe = splitSourceLink(normalizeImportedRecipe(raw) as unknown as Recipe);
  if (gaps.times) recipe = { ...recipe, activeTime: 0, cookTime: 0 };
  return { recipe, step: "review", unclear: [], gaps, origin: null };
}

export function RecipeWriterPage({ mode }: { mode: Mode }) {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mp = useMealPlanner();
  const key = mode === "edit" ? id : mode;
  const existing = mode === "edit" ? mp.recipeById(id) : undefined;

  const [draft, setDraft] = useState<Draft | null>(null);
  const [offer, setOffer] = useState<(Draft & { updatedAt: string }) | null>(null);
  const [batch, setBatch] = useState<Draft[] | null>(null);
  const [draftState, setDraftState] = useState<"clean" | "pending" | "saved" | "failed">("clean");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const started = useRef(false);
  const finished = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const startFresh = (): Draft => (existing ? { recipe: { ...existing }, step: 1, unclear: [], gaps: NO_GAPS, origin: null } : { recipe: blankRecipe(), step: 1, unclear: [], gaps: { servings: false, times: true }, origin: null });

  // Start once the library is loaded: an import handed over, a draft to
  // offer, or a fresh start.
  useEffect(() => {
    if (!mp.loaded || started.current) return;
    started.current = true;
    const handoff = location.state as ImportHandoff | null;
    if (mode === "import" && handoff?.recipes?.length) {
      // Drop the handoff so a reload continues from the saved draft.
      navigate(location.pathname, { replace: true, state: null });
      if (handoff.recipes.length > 1) {
        setBatch(handoff.recipes.map(fromImport));
        return;
      }
      setDraft({ ...fromImport(handoff.recipes[0]), origin: handoff.urlMeta ?? null });
      setDraftState("pending");
      return;
    }
    loadRecipeDraft(mp.userId, key)
      .catch(() => null)
      .then((saved) => {
        if (saved?.recipe) setOffer(saved);
        else if (mode === "import") navigate("/recipes", { replace: true });
        else setDraft(startFresh());
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mp.loaded]);

  // Keep the draft about a second after the last change.
  useEffect(() => {
    if (!draft || draftState !== "pending") return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (finished.current) return;
      saveRecipeDraft(mp.userId, key, { recipe: draft.recipe, step: draft.step, unclear: draft.unclear, gaps: draft.gaps, origin: draft.origin })
        .then(() => !finished.current && setDraftState("saved"))
        .catch(() => setDraftState("failed"));
    }, DRAFT_DELAY_MS);
    return () => clearTimeout(timer.current);
  }, [draft, draftState, mp.userId, key]);

  const cuisines = useMemo(() => cuisinesIn(mp.recipes), [mp.recipes]);
  const change = (fn: (d: Draft) => Draft) => {
    setDraft((d) => (d ? fn(d) : d));
    setDraftState("pending");
  };
  const setRecipe = (patch: Partial<Recipe>) => change((d) => ({ ...d, recipe: { ...d.recipe, ...patch } }));
  const goTo = (step: Step) => {
    change((d) => ({ ...d, step }));
    window.scrollTo(0, 0);
  };
  const leave = () => navigate(mode === "edit" ? `/recipes/${id}` : "/recipes");

  async function discard() {
    finished.current = true;
    clearTimeout(timer.current);
    await deleteRecipeDraft(mp.userId, key).catch(() => undefined);
    leave();
  }

  async function save() {
    if (!draft || saving) return;
    const recipe = readyToSave(draft.recipe);
    if (!recipe.title) return;
    setSaving(true);
    setError("");
    try {
      let savedId: string;
      if (draft.origin?.dedupRecipeId) {
        // Someone already imported this link: add their copy, not a duplicate.
        await mp.claimRecipeUrlImport(draft.origin.dedupRecipeId);
        savedId = draft.origin.dedupRecipeId;
      } else if (mode === "import") {
        const created = await mp.importRecipes([recipe]);
        savedId = created?.[0]?.id ?? "";
        if (draft.origin?.normalizedUrl && savedId) mp.registerRecipeUrlImport(draft.origin.normalizedUrl, savedId);
      } else {
        savedId = await mp.saveRecipe(recipe);
      }
      finished.current = true;
      clearTimeout(timer.current);
      await deleteRecipeDraft(mp.userId, key).catch(() => undefined);
      navigate(savedId ? `/recipes/${savedId}` : "/recipes", { replace: true });
    } catch (e) {
      setError((e as Error).message || "That didn't save. Your draft is kept; try again.");
    } finally {
      setSaving(false);
    }
  }

  async function addBatch() {
    if (!batch || saving) return;
    setSaving(true);
    setError("");
    try {
      await mp.importRecipes(batch.map((d) => readyToSave(d.recipe)).filter((r) => r.title));
      navigate("/recipes", { replace: true });
    } catch (e) {
      setError((e as Error).message || "Those didn't save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  const page = (body: ReactNode) => (
    <div className="mp-sans" style={{ maxWidth: 640, margin: "0 auto", display: "flex", flexDirection: "column", gap: SPACE.xxl, paddingBottom: SPACE.x5 }}>
      {body}
    </div>
  );

  if (!mp.loaded) return page(<p style={supporting}>Loading…</p>);

  if (mode === "edit" && (!existing || !mp.canEditRecipeContent(existing))) {
    return page(
      <>
        <p style={supporting}>{existing ? "This recipe can't be edited: it's public, or shared with you as view-only." : "This recipe isn't in your library."}</p>
        <Link to={existing ? `/recipes/${id}` : "/recipes"} style={textButton}>
          ‹ Back
        </Link>
      </>
    );
  }

  if (batch) {
    return page(
      <>
        <div>
          <h1 style={stepHeading}>Add {batch.length} recipes</h1>
          <p style={{ ...supporting, margin: "var(--space-xs) 0 0" }}>Found in the file. Nothing is saved until you tap Add. Flagged recipes can be fixed after saving.</p>
        </div>
        <div>
          {batch.map((d, n) => {
            const flags = reviewFlags(d.recipe, d.unclear, d.gaps);
            return (
              <div key={n} style={{ display: "flex", alignItems: "center", gap: SPACE.md, padding: "var(--space-md) 0", borderBottom: `1px solid ${COLORS.line}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--font-serif)", fontWeight: 600 }}>{d.recipe.title || "No name"}</div>
                  <div style={{ ...supporting, color: flags.length ? COLORS.caution : COLORS.soft }}>{flags.length ? flags.map((f) => f.text).join(" ") : `${(d.recipe.ingredients || []).length} ingredients · ${(d.recipe.instructions || []).length} steps`}</div>
                </div>
                <button type="button" aria-label={`Leave out ${d.recipe.title}`} onClick={() => setBatch((b) => (b && b.length > 1 ? b.filter((_, i) => i !== n) : b))} style={{ ...textButton, color: COLORS.danger, fontSize: FONT_SIZE.h4 }}>
                  ×
                </button>
              </div>
            );
          })}
        </div>
        {error && <p style={{ color: COLORS.danger, margin: 0 }}>{error}</p>}
        <div style={{ display: "flex", gap: SPACE.md }}>
          <button type="button" onClick={addBatch} disabled={saving} style={{ ...primaryButton, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Adding…" : `Add ${batch.length} recipes`}
          </button>
          <button type="button" onClick={() => navigate("/recipes")} style={secondaryButton}>
            Cancel
          </button>
        </div>
      </>
    );
  }

  if (offer) {
    return page(
      <>
        <h1 style={stepHeading}>{mode === "edit" ? "You have unsaved changes" : "You have a draft"}</h1>
        <p style={{ ...supporting, margin: 0 }}>
          {offer.recipe.title ? `“${offer.recipe.title}”` : "A recipe with no name yet"}, last changed {new Date(offer.updatedAt).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.
        </p>
        <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => {
              setDraft({ recipe: offer.recipe, step: offer.step || 1, unclear: offer.unclear || [], gaps: offer.gaps || NO_GAPS, origin: offer.origin || null });
              setOffer(null);
              setDraftState("saved");
            }}
            style={primaryButton}
          >
            Continue {mode === "edit" ? "editing" : "your draft"}
          </button>
          <button
            type="button"
            onClick={() => {
              deleteRecipeDraft(mp.userId, key).catch(() => undefined);
              setOffer(null);
              if (mode === "import") navigate("/recipes", { replace: true });
              else setDraft(startFresh());
            }}
            style={secondaryButton}
          >
            {mode === "edit" ? "Start from the saved recipe" : "Start over"}
          </button>
        </div>
      </>
    );
  }

  if (!draft) return page(<p style={supporting}>Loading…</p>);

  const { recipe, step } = draft;
  const flags = reviewFlags(recipe, draft.unclear, draft.gaps);
  const back: { label: string; to: Step | null } = step === "review" ? { label: "‹ Details", to: 4 } : step === 1 ? { label: "Cancel", to: null } : { label: `‹ ${STEP_NAMES[(step - 1) as 1 | 2 | 3]}`, to: (step - 1) as Step };
  const draftNote = draftState === "pending" ? "saving the draft…" : draftState === "failed" ? "the draft couldn't be saved" : draftState === "saved" ? "saved as a draft" : "";

  return page(
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md, borderBottom: `1px solid ${COLORS.line}` }}>
        <button type="button" onClick={() => (back.to ? goTo(back.to) : leave())} style={textButton}>
          {back.label}
        </button>
        <span style={{ ...supporting, color: draftState === "failed" ? COLORS.caution : COLORS.soft, textAlign: "right" }}>
          {step === "review" ? "Review" : `Step ${step} of 4`}
          {draftNote ? ` · ${draftNote}` : ""}
        </span>
      </div>

      {step === 1 && <BasicsStep recipe={recipe} onChange={setRecipe} />}
      {step === 2 && <IngredientsStep items={(recipe.ingredients || []) as EntryIngredient[]} unclear={draft.unclear} onChange={(ingredients, unclear) => change((d) => ({ ...d, unclear, recipe: { ...d.recipe, ingredients } }))} />}
      {step === 3 && <StepsStep steps={(recipe.instructions || []) as EntryStep[]} onChange={(instructions) => setRecipe({ instructions })} />}
      {step === 4 && <DetailsStep recipe={recipe} onChange={setRecipe} cuisines={cuisines} />}
      {step === "review" &&
        (draft.origin?.dedupRecipeId ? (
          <div>
            <h1 style={stepHeading}>Already in The Weekly Kitchen</h1>
            <p style={{ ...supporting, margin: "var(--space-xs) 0 0" }}>Someone already imported “{recipe.title}” from this link. Adding it puts their copy in your library rather than making a duplicate, so changes can&apos;t be made here.</p>
          </div>
        ) : (
          <RecipeReview recipe={recipe} flags={flags} unclear={draft.unclear} importedFrom={mode === "import" ? recipe.sourceUrl || null : null} onEdit={goTo} />
        ))}

      {error && <p style={{ color: COLORS.danger, margin: 0 }}>{error}</p>}

      <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap", alignItems: "center" }}>
        {step !== "review" ? (
          <button type="button" onClick={() => goTo(step === 4 ? "review" : ((step + 1) as Step))} style={primaryButton}>
            {step === 4 ? "Review and save" : `Next: ${STEP_NAMES[(step + 1) as 2 | 3 | 4].toLowerCase()}`}
          </button>
        ) : (
          <>
            <button type="button" onClick={save} disabled={saving || (!draft.origin?.dedupRecipeId && !recipe.title.trim())} style={{ ...primaryButton, opacity: saving || (!draft.origin?.dedupRecipeId && !recipe.title.trim()) ? 0.6 : 1 }}>
              {saving ? "Saving…" : draft.origin?.dedupRecipeId ? "Add their copy" : mode === "edit" ? "Save changes" : "Save recipe"}
            </button>
            <button type="button" onClick={discard} style={{ ...textButton, color: COLORS.soft }}>
              {mode === "edit" ? "Discard changes" : "Discard"}
            </button>
          </>
        )}
      </div>
      {step === "review" && flags.length > 0 && !draft.origin?.dedupRecipeId && <p style={{ ...supporting, margin: 0 }}>You can save now and fix the flagged items later.</p>}
    </>
  );
}

// /recipes/:id/edit, started afresh for each recipe.
export function RecipeEditPage() {
  const { id = "" } = useParams();
  return <RecipeWriterPage key={id} mode="edit" />;
}
